"use strict";
/**
 * Idempotency keys for money requests: card and check payments, new orders,
 * PayPal creates and refunds.
 *
 * The API replays a resent money request that carries the same `request_id`
 * (a UUID, sent as a body field beside `payment` or `payment_amount`) and the
 * same body: it answers as it did the first time and charges nothing again.
 * The same key with another body is refused (409 `idempotency_key_reused`).
 * So a retry must resend the exact body the key was first sent with, even
 * when the card was tokenised again; {@link PaymentRequest} holds that body.
 *
 * The rule is drivescout-vue3's (`src/features/payments/paymentTarget.ts`),
 * written up in drivescout-api
 * `dev-notes/order-payments-void-refund/16_CLIENT_IDEMPOTENCY_KEYS_PLAN.md`.
 * This module is framework-free so AngularJS controllers and Vue components
 * share it.
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PaymentRequest = exports.PaymentOutcome = void 0;
exports.newRequestId = newRequestId;
exports.errorIdOf = errorIdOf;
exports.withoutOneTimeToken = withoutOneTimeToken;
exports.orderWithoutOneTimeTokens = orderWithoutOneTimeTokens;
/**
 * Answers after which the held key can never succeed, so the next send takes
 * a new one. Every other failure keeps the key: a lost answer, a 5xx,
 * `payment_outcome_unknown`, `payment_in_progress`,
 * `financial_operation_in_progress` or a 422, where a resend replays or is
 * simply checked again.
 */
var RENEWING_ERROR_IDS = [
    // A declined key replays its decline, so a retry needs a new key.
    "payment_failed",
    // The key is held for another body: this request never ran under it.
    "idempotency_key_reused",
    // Refunds: the processor declined, a kept id whose decline answer was
    // lost, or an id already spent on other details.
    "refund_declined",
    "refund_request_declined",
    "refund_request_reused",
];
/**
 * Stripe error types that definitely refuse a 3-D Secure step: the card, the
 * request (a failed authentication) or Stripe.js's own field checks. The
 * intent took nothing and needs a new card.
 */
var REFUSING_STRIPE_ERROR_TYPES = [
    "card_error",
    "invalid_request_error",
    "validation_error",
];
/**
 * The one refusing-typed error that is no refusal: the intent is no longer
 * awaiting the customer, which a replayed challenge meets when the first one
 * already succeeded.
 */
var UNSETTLED_STRIPE_ERROR_CODE = "payment_intent_unexpected_state";
/** What a payment's details hold in place of the card's one-time token. */
var ONE_TIME_TOKEN_PLACEHOLDER = "one-time token";
/** AngularJS adds `$$hashKey` to repeated objects; `angular.toJson` never sends such keys. */
var ANGULAR_INTERNAL_KEY_PREFIX = "$$";
var UUID_BYTE_LENGTH = 16;
var UUID_VERSION_BYTE = 6;
var UUID_VARIANT_BYTE = 8;
var UUID_VERSION_4_BITS = 0x40;
var UUID_VARIANT_RFC4122_BITS = 0x80;
var LOW_NIBBLE_MASK = 0x0f;
var LOW_SIX_BITS_MASK = 0x3f;
var HEX_RADIX = 16;
/** A new request id: a lowercase v4 UUID. */
function newRequestId() {
    // randomUUID exists only in a secure context (https or localhost);
    // getRandomValues exists on an http origin too.
    if (typeof crypto.randomUUID === "function") {
        return crypto.randomUUID().toLowerCase();
    }
    return uuidV4FromRandomValues();
}
function uuidV4FromRandomValues() {
    var bytes = crypto.getRandomValues(new Uint8Array(UUID_BYTE_LENGTH));
    bytes[UUID_VERSION_BYTE] =
        (bytes[UUID_VERSION_BYTE] & LOW_NIBBLE_MASK) | UUID_VERSION_4_BITS;
    bytes[UUID_VARIANT_BYTE] =
        (bytes[UUID_VARIANT_BYTE] & LOW_SIX_BITS_MASK) | UUID_VARIANT_RFC4122_BITS;
    var hex = Array.from(bytes, function (byte) {
        return ("0" + byte.toString(HEX_RADIX)).slice(-2);
    }).join("");
    return [
        hex.slice(0, 8),
        hex.slice(8, 12),
        hex.slice(12, 16),
        hex.slice(16, 20),
        hex.slice(20),
    ].join("-");
}
/**
 * The API's error id (`config/errors.php`) on a failed request, or null when
 * the answer carried none or never arrived. AngularJS `$http` rejects with
 * the response (`error.data`); axios wraps it (`error.response.data`).
 */
function errorIdOf(error) {
    if (typeof error !== "object" || error === null) {
        return null;
    }
    var failure = error;
    var body = typeof failure.response === "object" && failure.response !== null
        ? failure.response.data
        : failure.data;
    if (typeof body !== "object" || body === null) {
        return null;
    }
    var id = body.id;
    return typeof id === "string" ? id : null;
}
/**
 * A payment as the payer chose it, for {@link PaymentRequest.bodyFor}'s
 * details: the card's one-time token (a Stripe confirmation token, a Square
 * nonce, Authorize.Net opaque data, PayPal's create data) is minted anew by
 * each click for the same card, so it stands as a placeholder. A typed card,
 * a saved card's id and the save box are the payer's choice and stay whole.
 */
function withoutOneTimeToken(payment) {
    if (!payment.card || payment.card.token == null) {
        return payment;
    }
    return __assign(__assign({}, payment), { card: __assign(__assign({}, payment.card), { token: ONE_TIME_TOKEN_PLACEHOLDER }) });
}
/** A new order's details: each of its payments without the card's one-time token. */
function orderWithoutOneTimeTokens(order) {
    return __assign(__assign({}, order), { payments: order.payments.map(withoutOneTimeToken) });
}
exports.PaymentOutcome = {
    /** A success seen without its answer: PayPalCard captured a new order itself. */
    succeeded: function () {
        return { kind: "succeeded" };
    },
    /**
     * A 2xx answer: a payment, a confirmed 3-D Secure payment, a PayPal capture
     * or a refund succeeded; but a 202 `requires_action` still awaits the
     * cardholder's 3-D Secure step, so its key is kept until the confirm ends.
     */
    answered: function (response) {
        var awaitsThreeDSecure = typeof response === "object" &&
            response !== null &&
            response.requires_action === true;
        return awaitsThreeDSecure
            ? { kind: "awaiting_action" }
            : { kind: "succeeded" };
    },
    /** The request (or its 3-D Secure confirm, or a PayPal capture) was refused or its answer lost. */
    failed: function (error) {
        return { kind: "failed", error: error };
    },
    /** The cardholder's 3-D Secure step did not authenticate, or could not start. */
    challengeFailed: function (failure) {
        return { kind: "challenge_failed", failure: failure };
    },
};
/**
 * Whether a key is spent: after a success, a definite decline, a key held
 * for another body, or a 3-D Secure refusal Stripe itself returned. Doubt
 * keeps the key, which is the money-safe side: a resend replays what the
 * first request became, while a new key could charge twice.
 */
function endsTheKey(outcome) {
    switch (outcome.kind) {
        case "succeeded":
            return true;
        case "awaiting_action":
            return false;
        case "failed": {
            var errorId = errorIdOf(outcome.error);
            return errorId !== null && RENEWING_ERROR_IDS.indexOf(errorId) !== -1;
        }
        case "challenge_failed": {
            var _a = outcome.failure, errorType = _a.errorType, errorCode = _a.errorCode;
            return (errorType !== undefined &&
                REFUSING_STRIPE_ERROR_TYPES.indexOf(errorType) !== -1 &&
                errorCode !== UNSETTLED_STRIPE_ERROR_CODE);
        }
    }
}
/** Objects with their keys sorted at every depth, so equal details spell the same. */
function sortedByKey(value) {
    if (Array.isArray(value)) {
        return value.map(sortedByKey);
    }
    if (typeof value !== "object" || value === null) {
        return value;
    }
    var record = value;
    var sorted = {};
    Object.keys(record)
        .filter(function (key) { return key.indexOf(ANGULAR_INTERNAL_KEY_PREFIX) !== 0; })
        .sort()
        .forEach(function (key) {
        sorted[key] = sortedByKey(record[key]);
    });
    return sorted;
}
/** The data as JSON sends it: no undefined fields, dates as strings, no shared references. */
function asSent(value) {
    return JSON.parse(JSON.stringify(value));
}
function comparable(details) {
    return JSON.stringify(sortedByKey(asSent(details)));
}
/**
 * One kind of money request on one screen, and the key held between its
 * sends. The controller or component that makes the call and sees its answer
 * owns the instance for the screen's lifetime; nothing is stored, so a reload
 * starts over. A card payment and a PayPal approval hold separate instances.
 *
 * - {@link bodyFor}: the body to send. While a key is held for the same
 *   details, that is the held body (the first token included) under the held
 *   key; for new or changed details, a new key and a new body.
 * - {@link settled}: report how the request ended. A 202 that awaits 3-D
 *   Secure keeps the key until its confirm ends; a PayPal approval keeps it
 *   until its capture ends.
 */
var PaymentRequest = /** @class */ (function () {
    function PaymentRequest() {
        this.held = null;
    }
    /**
     * @param details what the payer chose, compared as JSON with sorted keys:
     *   the body plus its target (an order or fee in the path), with the card's
     *   one-time token left out ({@link withoutOneTimeToken})
     * @param build the body for a new key, `request_id` included
     */
    PaymentRequest.prototype.bodyFor = function (details, build) {
        var comparableDetails = comparable(details);
        if (this.held === null || this.held.details !== comparableDetails) {
            this.held = {
                details: comparableDetails,
                body: asSent(build(newRequestId())),
            };
        }
        return asSent(this.held.body);
    };
    PaymentRequest.prototype.settled = function (outcome) {
        if (endsTheKey(outcome)) {
            this.held = null;
        }
    };
    return PaymentRequest;
}());
exports.PaymentRequest = PaymentRequest;
